import posthog from "posthog-js";

/**
 * Track a custom event with PostHog
 * Automatically includes user context (non-PII) from Redux store
 *
 * @param {string} eventName - Name of the event to track
 * @param {object} properties - Additional properties to include with the event
 * @param {object} userContext - User context from Redux store (optional)
 */
export const trackEvent = (eventName, properties = {}, userContext = null) => {
  try {
    // Only track if PostHog is initialized
    if (!posthog.__loaded) {
      console.warn("PostHog not initialized, skipping event tracking");
      return;
    }

    // Build event properties with non-PII user context
    const eventProps = { ...properties };

    if (userContext) {
      // Add non-PII user context
      if (userContext.id) {
        eventProps.user_id = userContext.id;
      }

      if (userContext.account?.id) {
        eventProps.account_id = userContext.account.id;
      }

      // Add roles as array of role names
      if (userContext.roles && Array.isArray(userContext.roles)) {
        eventProps.user_roles = userContext.roles.map(
          (role) => role.name || role.id,
        );
        eventProps.user_role_primary = eventProps.user_roles[0] || "none";
      }
    }

    // Track the event
    posthog.capture(eventName, eventProps);
  } catch (error) {
    console.error("Error tracking event:", error);
  }
};

/**
 * Identify user in PostHog (using numeric ID only, no PII)
 *
 * @param {number} userId - Numeric user ID
 * @param {object} properties - Additional non-PII properties
 */
export const identifyUser = (userId, properties = {}) => {
  try {
    if (!posthog.__loaded) {
      console.warn("PostHog not initialized, skipping user identification");
      return;
    }

    // Identify user with numeric ID only
    posthog.identify(userId.toString(), properties);
  } catch (error) {
    console.error("Error identifying user:", error);
  }
};

/**
 * Reset PostHog session (call on logout)
 */
export const resetAnalytics = () => {
  try {
    if (!posthog.__loaded) {
      return;
    }
    posthog.reset();
  } catch (error) {
    console.error("Error resetting analytics:", error);
  }
};

const analytics = {
  trackEvent,
  identifyUser,
  resetAnalytics,
};

export default analytics;
